"use client";

import { useEffect, useId, useMemo, useRef, useState } from "react";
import { Ellipsis, RotateCcw, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useCreatePageStore } from "@/stores/createPageStore";
import {
  applyDesignDetailPathEdit,
  canEditDesignDetailPath,
} from "@/lib/designer/designDetails";
import { compileCurrentDesignSpec } from "@/services/turn/brief/briefForm";
import { getDesignSpecFieldLabel } from "@/lib/designer/previewFreshness";
import { projectDesignDetails } from "@/lib/designer/designDetailRows";
import type { projectDesignerWorkspaceActions } from "@/lib/designer/workspaceActions";
import { cn } from "@/lib/utils";
import type { DesignSpecFieldChange } from "@/types/generatedImage";
import type { PendingDesignAction } from "./PendingDesignActionDialog";

const EDIT_PATH_BY_LABEL: Record<string, string> = {
  Color: "visual.primaryColor",
  Silhouette: "visual.silhouette",
  Fabric: "visual.fabric",
  Sleeves: "visual.sleeve",
  "Front neckline": "visual.neckline",
  Length: "visual.length",
  Waist: "visual.waistline",
  Back: "visual.backPrompt",
  "Back direction": "visual.backPrompt",
  "Back style": "visual.backStyle",
  "Back closure": "visual.closure",
  "Must have": "constraints.mustHave",
  Avoid: "constraints.avoid",
  Occasion: "occasion",
  "Event date": "eventDate",
  "Wearer details": "wearerContext",
  "Overall design": "visual.prompt",
  "Accent colors": "visual.accentColors",
  "Sleeve details": "visual.sleeveAddons",
  Mood: "visual.mood",
  "Design details": "visual.details",
  Comfort: "constraints.comfort",
  Mobility: "constraints.mobility",
  "Fit considerations": "constraints.bodyConcerns",
  Coverage: "constraints.modesty",
  "Coverage details": "constraints.coverage",
  Support: "constraints.support",
  Opacity: "constraints.opacity",
  Accessibility: "constraints.accessibility",
  "Cultural details": "constraints.cultural",
  Construction: "constraints.construction",
  Budget: "constraints.budget",
  "Reference guidance": "referenceGuidance",
};

function readable(value: unknown): string {
  if (typeof value === "string") return value.replaceAll("_", " ").trim();
  if (Array.isArray(value))
    return value.map(readable).filter(Boolean).join(", ");
  return "";
}

export function pendingDesignSummary(changes: DesignSpecFieldChange[]): string {
  const unique = [
    ...new Map(changes.map(change => [change.path, change])).values(),
  ];
  const specific = unique.filter(change => change.path !== "visual.prompt");
  const summary = specific.length ? specific : unique;
  return (
    summary
      .slice(0, 2)
      .map(change => {
        const value = readable(change.draftValue);
        return value && value.length <= 48
          ? value
          : getDesignSpecFieldLabel(change.path);
      })
      .join(" · ") + (summary.length > 2 ? ` +${summary.length - 2} more` : "")
  );
}

/** Pending values describe the next preview; saved image contracts stay immutable. */
export default function DesignDetailsInspector({
  side,
  changes,
  onClose,
  onEditInChat,
  onEditingChange,
  onRevertChanges,
  onStartOver,
  startOverDisabled = false,
  secondaryActions,
  onSecondaryAction,
  showHeader = true,
  compact = false,
}: {
  side: "front" | "back";
  changes: DesignSpecFieldChange[];
  onClose?: () => void;
  onEditInChat?: () => void;
  onEditingChange?: (editing: boolean) => void;
  onRevertChanges?: () => void;
  onStartOver?: () => void;
  startOverDisabled?: boolean;
  secondaryActions?: ReturnType<typeof projectDesignerWorkspaceActions>;
  onSecondaryAction?: (
    action: PendingDesignAction,
    trigger: HTMLButtonElement
  ) => void;
  showHeader?: boolean;
  compact?: boolean;
}) {
  const titleId = useId();
  const [editingPath, setEditingPath] = useState<string | null>(null);
  const [draftValue, setDraftValue] = useState("");
  const [editingRowHeight, setEditingRowHeight] = useState(0);
  const editingPathRef = useRef<string | null>(null);
  const originalValueRef = useRef("");
  const inputRef = useRef<HTMLInputElement | HTMLSelectElement | null>(null);
  const rowRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const restoreRowFocusRef = useRef<string | null>(null);
  const menuTriggerRef = useRef<HTMLButtonElement>(null);
  const startOverAfterMenuClose = useRef(false);
  const secondaryAfterMenuClose = useRef<PendingDesignAction | null>(null);
  const revertAfterMenuClose = useRef(false);
  useEffect(() => {
    onEditingChange?.(Boolean(editingPath));
    return () => onEditingChange?.(false);
  }, [editingPath, onEditingChange]);
  useEffect(() => {
    if (editingPath) {
      inputRef.current?.focus({ preventScroll: true });
      return;
    }
    const path = restoreRowFocusRef.current;
    if (path) {
      restoreRowFocusRef.current = null;
      rowRefs.current[path]?.focus({ preventScroll: true });
    }
  }, [editingPath]);
  const form = useCreatePageStore(state => state.form.data);
  const brief = useCreatePageStore(state => state.agent.designBrief);
  const images = useCreatePageStore(state => state.generation.images);
  const draft = useMemo(
    () =>
      compileCurrentDesignSpec(brief, form, {
        hasFrontImage: Boolean(images.front.url),
        hasBackImage: Boolean(images.back.url),
      }),
    [brief, form, images.front.url, images.back.url]
  );
  const disabled = useCreatePageStore(
    state =>
      state.generation.progress.loading ||
      state.ui.modification.isModifying ||
      state.agent.conversation.isTyping ||
      state.agent.conversation.presetsLoading ||
      Boolean(state.agent.activeRequestController) ||
      state.ticket.designSubmitted
  );
  const beginEditing = (path: string, value: string) => {
    if (disabled || editingPathRef.current) return;
    editingPathRef.current = path;
    setEditingRowHeight(
      rowRefs.current[path]?.getBoundingClientRect().height ?? 0
    );
    originalValueRef.current = value === "Removed" ? "" : value;
    setDraftValue(originalValueRef.current);
    setEditingPath(path);
  };
  const finishEditing = (
    path: string,
    save: boolean,
    restoreFocus: boolean
  ) => {
    if (editingPathRef.current !== path) return;
    editingPathRef.current = null;
    if (
      save &&
      !disabled &&
      draftValue.trim() !== originalValueRef.current.trim()
    ) {
      useCreatePageStore.setState(state => {
        applyDesignDetailPathEdit(state, path, draftValue);
      });
    }
    restoreRowFocusRef.current = restoreFocus ? path : null;
    setEditingPath(null);
  };
  const {
    rows: fields,
    pendingCount,
    hasSavedPreview: hasSavedDesign,
  } = projectDesignDetails({
    side,
    draft: draft ?? null,
    images,
    changes,
  });
  const canTryOn = Boolean(
    onSecondaryAction && secondaryActions?.tryOn.enabled
  );
  const canQuote = Boolean(
    onSecondaryAction && secondaryActions?.quote.enabled
  );
  const hasSecondaryActions = canTryOn || canQuote;
  const canRevert = Boolean(onRevertChanges && !editingPath);
  return (
    <aside
      aria-labelledby={titleId}
      data-slot="design-details-inspector"
      className="flex h-full min-h-0 min-w-0 flex-col"
    >
      <div
        className={
          showHeader
            ? cn(
                "flex h-11 shrink-0 items-center justify-between gap-2",
                compact ? "mb-2" : "mb-4"
              )
            : "sr-only"
        }
      >
        <h2 id={titleId} className="text-base font-semibold">
          Design details
        </h2>
        {compact &&
        (onEditInChat || onStartOver || hasSecondaryActions || canRevert) ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                ref={menuTriggerRef}
                variant="ghost"
                size="icon"
                className="designer-control size-11"
                aria-label="More details actions"
              >
                <Ellipsis className="size-4" aria-hidden />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent
              align="end"
              onCloseAutoFocus={event => {
                const secondary = secondaryAfterMenuClose.current;
                const revert = revertAfterMenuClose.current;
                const startOver = startOverAfterMenuClose.current;
                if (!startOver && !secondary && !revert) return;
                secondaryAfterMenuClose.current = null;
                revertAfterMenuClose.current = false;
                startOverAfterMenuClose.current = false;
                event.preventDefault();
                menuTriggerRef.current?.focus({ preventScroll: true });
                if (secondary) {
                  if (!disabled && menuTriggerRef.current)
                    onSecondaryAction?.(secondary, menuTriggerRef.current);
                } else if (revert) {
                  if (!disabled && !editingPathRef.current) onRevertChanges?.();
                } else {
                  if (!disabled && !startOverDisabled) onStartOver?.();
                }
              }}
            >
              {onEditInChat ? (
                <DropdownMenuItem className="min-h-11" onSelect={onEditInChat}>
                  Edit in chat
                </DropdownMenuItem>
              ) : null}
              {canRevert ? (
                <DropdownMenuItem
                  className="min-h-11"
                  disabled={disabled}
                  onSelect={() => {
                    revertAfterMenuClose.current = true;
                  }}
                >
                  Revert changes
                </DropdownMenuItem>
              ) : null}
              {hasSecondaryActions ? (
                <>
                  {onEditInChat || canRevert ? <DropdownMenuSeparator /> : null}
                  {canTryOn ? (
                    <DropdownMenuItem
                      className="min-h-11"
                      onSelect={() => {
                        secondaryAfterMenuClose.current = "tryOn";
                      }}
                    >
                      Try on
                    </DropdownMenuItem>
                  ) : null}
                  {canQuote ? (
                    <DropdownMenuItem
                      className="min-h-11"
                      onSelect={() => {
                        secondaryAfterMenuClose.current = "quote";
                      }}
                    >
                      Get quote
                    </DropdownMenuItem>
                  ) : null}
                </>
              ) : null}
              {onStartOver ? (
                <>
                  {onEditInChat || canRevert || hasSecondaryActions ? (
                    <DropdownMenuSeparator />
                  ) : null}
                  <DropdownMenuItem
                    className="min-h-11"
                    disabled={startOverDisabled || disabled}
                    onSelect={() => {
                      startOverAfterMenuClose.current = true;
                    }}
                  >
                    <RotateCcw className="size-4" aria-hidden />
                    Start over
                  </DropdownMenuItem>
                </>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
        {!compact && onClose && showHeader ? (
          <Button
            variant="ghost"
            size="icon"
            className="designer-control size-11 text-muted-foreground"
            aria-label="Close design details"
            onClick={onClose}
          >
            <X className="size-4" aria-hidden />
          </Button>
        ) : null}
      </div>
      <>
        <div className="designer-details-scroll min-h-0 flex-1 overflow-y-auto overscroll-contain">
          {!compact && pendingCount > 0 ? (
            <div
              className="mb-3 rounded-xl bg-muted px-3.5 py-3 text-sm font-medium"
              data-slot="design-pending-summary"
              role="status"
              aria-live="polite"
              aria-atomic="true"
            >
              {pendingCount} pending {pendingCount === 1 ? "change" : "changes"}
            </div>
          ) : null}
          {!compact ? (
            <div className="flex min-h-11 items-center gap-2">
              <h3 className="text-xs font-normal text-muted-foreground">
                {pendingCount
                  ? "Updated details"
                  : hasSavedDesign
                    ? "Current dress"
                    : "Design details"}
              </h3>
            </div>
          ) : null}
          <dl className="divide-y divide-border">
            {fields.map(field => {
              const path = EDIT_PATH_BY_LABEL[field.label];
              const editable = Boolean(path && canEditDesignDetailPath(path));
              const active = editable && editingPath === path;
              return (
                <div
                  key={field.label}
                  ref={element => {
                    if (path) rowRefs.current[path] = element;
                  }}
                  data-pending={field.pending ? "true" : undefined}
                  data-slot="design-detail-row"
                  style={
                    active && editingRowHeight > 0
                      ? { minHeight: editingRowHeight }
                      : undefined
                  }
                  className={cn(
                    "grid min-h-11 grid-cols-[minmax(0,1fr)_minmax(0,1.8fr)] items-center gap-3 px-2.5 py-1 text-sm leading-5",
                    field.pending && "designer-detail-pending rounded-lg",
                    editable &&
                      !active &&
                      !disabled &&
                      "cursor-text hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                  )}
                  role={editable && !active ? "button" : undefined}
                  tabIndex={editable && !active ? 0 : undefined}
                  aria-label={
                    editable && !active ? `Edit ${field.label}` : undefined
                  }
                  aria-disabled={editable && !active ? disabled : undefined}
                  onClick={() => {
                    if (editable && path && !active)
                      beginEditing(path, field.value);
                  }}
                  onKeyDown={event => {
                    if (!editable || !path || active || disabled) return;
                    if (event.key !== "Enter" && event.key !== " ") return;
                    event.preventDefault();
                    beginEditing(path, field.value);
                  }}
                >
                  <dt className="text-muted-foreground">{field.label}</dt>
                  <dd className="min-w-0 break-words text-right">
                    {active && path ? (
                      path === "constraints.modesty" ? (
                        <select
                          ref={element => {
                            inputRef.current = element;
                          }}
                          aria-label={field.label}
                          value={draftValue}
                          disabled={disabled}
                          onChange={event => {
                            const value = event.target.value;
                            setDraftValue(value);
                            editingPathRef.current = null;
                            useCreatePageStore.setState(state => {
                              applyDesignDetailPathEdit(state, path, value);
                            });
                            restoreRowFocusRef.current = path;
                            setEditingPath(null);
                          }}
                          onBlur={() => finishEditing(path, false, false)}
                          onKeyDown={event => {
                            if (event.key !== "Escape") return;
                            event.preventDefault();
                            event.stopPropagation();
                            finishEditing(path, false, true);
                          }}
                          className="h-8 w-full rounded-md border border-border bg-background px-2 text-right text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                          <option value="">Not set</option>
                          <option value="modest">Modest</option>
                          <option value="balanced">Balanced</option>
                          <option value="revealing">Revealing</option>
                        </select>
                      ) : (
                        <input
                          ref={element => {
                            inputRef.current = element;
                          }}
                          aria-label={field.label}
                          value={draftValue}
                          disabled={disabled}
                          onChange={event => setDraftValue(event.target.value)}
                          onBlur={() => finishEditing(path, true, false)}
                          onKeyDown={event => {
                            if (event.key !== "Enter" && event.key !== "Escape")
                              return;
                            event.preventDefault();
                            event.stopPropagation();
                            finishEditing(path, event.key === "Enter", true);
                          }}
                          className="h-8 w-full min-w-0 rounded-md border border-border bg-background px-2 text-right text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        />
                      )
                    ) : (
                      <>
                        <span
                          className={field.pending ? "font-medium" : undefined}
                        >
                          {field.value || "Removed"}
                        </span>
                        {field.pending ? (
                          <span className="ml-1 text-[11px] leading-4 text-muted-foreground">
                            Pending
                          </span>
                        ) : null}
                      </>
                    )}
                  </dd>
                </div>
              );
            })}
          </dl>
          {!fields.length ? (
            <p className="py-3 text-sm text-muted-foreground">
              Your choices will appear here as you design.
            </p>
          ) : null}
        </div>
        {!compact ? (
          <div className="mt-3 shrink-0 lg:mt-4 lg:border-t lg:border-border lg:pt-4">
            <Button
              variant="outline"
              className="designer-control h-12 w-full bg-transparent"
              onClick={onEditInChat}
            >
              Edit in chat
            </Button>
          </div>
        ) : null}
      </>
    </aside>
  );
}
